import React, { useState, useEffect } from 'react';
import './AddRequisiteModal.css';
import { fetchDevicesData } from '../services/dataApi';
import DetailsPortal from './DetailsPortal';

const AddRequisiteModal = ({ isOpen, onClose, onSubmit, initialData = null, isEdit = false }) => {
  const [formData, setFormData] = useState({
    AccountNumber: '',
    Bank: '',
    Currency: '',
    NameSurname: '',
    device: '',
    status: 'active'
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [devices, setDevices] = useState([]);
  const [devicesLoading, setDevicesLoading] = useState(false);

  // Load the device list when opening the modal
  useEffect(() => {
    const loadDevices = async () => {
      if (isOpen) {
        setDevicesLoading(true);
        try {
          // Load all devices (large limit to fetch all of them)
          const devicesData = await fetchDevicesData(1, 1000);
          // Extract the device array from the response
          let devicesList = [];
          
          // Support the new paginated format
          if (devicesData && devicesData.data && Array.isArray(devicesData.data)) {
            devicesList = devicesData.data;
          } else if (Array.isArray(devicesData)) {
            devicesList = devicesData;
          } else if (devicesData && devicesData.ops && devicesData.ops.length > 0) {
            const firstOp = devicesData.ops[0];
            if (firstOp.list && Array.isArray(firstOp.list)) {
              devicesList = firstOp.list;
            } else if (firstOp.data && firstOp.data.list && Array.isArray(firstOp.data.list)) {
              devicesList = firstOp.data.list;
            }
          }
          setDevices(devicesList);
        } catch (err) {
          console.error('Error loading devices:', err);
          setDevices([]);
        } finally {
          setDevicesLoading(false);
        }
      }
    };
    
    loadDevices();
  }, [isOpen]);

  // Fill the form with data while editing
  useEffect(() => {
    if (isOpen && initialData && isEdit) {
      setFormData({
        AccountNumber: initialData.AccountNumber || initialData.accountNumber || '',
        Bank: initialData.Bank || initialData.bank || '',
        Currency: initialData.Currency || initialData.currency || '',
        NameSurname: initialData.NameSurname || initialData.nameSurname || initialData.name_surname || '',
        device: initialData.device || initialData.Device || '',
        status: initialData.status || initialData.Status || 'active'
      });
    } else if (isOpen && !isEdit) {
      // Reset the form when creating
      setFormData({
        AccountNumber: '',
        Bank: '',
        Currency: '',
        NameSurname: '',
        device: '',
        status: 'active'
      });
    }
  }, [isOpen, initialData, isEdit]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
    setError(null);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError(null);
    setLoading(true);

    try {
      // Validation
      if (!formData.AccountNumber || !formData.Bank || !formData.Currency || !formData.NameSurname || !formData.device) {
        setError('Please fill in all required fields');
        setLoading(false);
        return;
      }

      // When editing, also pass the original data
      const result = isEdit 
        ? await onSubmit(formData, initialData)
        : await onSubmit(formData);
      
      if (result.success) {
        // Clear the form and close the modal immediately after a successful response
        setFormData({
          AccountNumber: '',
          Bank: '',
          Currency: '',
          NameSurname: '',
          device: '',
          status: 'active'
        });
        setLoading(false);
        onClose();
      } else {
        setError(result.error || (isEdit ? 'Failed to update requisite' : 'Failed to create requisite'));
        setLoading(false);
      }
    } catch (err) {
      setError(err.message || 'An error occurred');
      setLoading(false);
    }
  };

  const handleClose = () => {
    if (!loading) {
      setFormData({
        AccountNumber: '',
        Bank: '',
        Currency: '',
        NameSurname: '',
        device: '',
        status: 'active'
      });
      setError(null);
      onClose();
    }
  };

  if (!isOpen) return null;

  return (
    <DetailsPortal>
      <div className="modal-overlay" onClick={handleClose}>
        <div className="modal-content" onClick={(e) => e.stopPropagation()}>
        <div className="modal-header">
          <h2>{isEdit ? 'Edit Requisite' : 'Add Requisite'}</h2>
          <button className="modal-close" onClick={handleClose} disabled={loading}>
            <svg viewBox="0 0 24 24" aria-hidden="true"><path d="M6 6l12 12M18 6L6 18" /></svg>
          </button>
        </div>
        
        <form onSubmit={handleSubmit} className="requisite-form">
          <div className="form-group">
            <label htmlFor="AccountNumber">
              Account Number <span className="required">*</span>
            </label>
            <input
              type="text"
              id="AccountNumber"
              name="AccountNumber"
              value={formData.AccountNumber}
              onChange={handleInputChange}
              required
              disabled={loading}
              className="form-input"
            />
          </div>

          <div className="form-group">
            <label htmlFor="Bank">
              Bank <span className="required">*</span>
            </label>
            <input
              type="text"
              id="Bank"
              name="Bank"
              value={formData.Bank}
              onChange={handleInputChange}
              required
              disabled={loading}
              className="form-input"
            />
          </div>

          <div className="form-group">
            <label htmlFor="Currency">
              Currency <span className="required">*</span>
            </label>
            <input
              type="text"
              id="Currency"
              name="Currency"
              value={formData.Currency}
              onChange={handleInputChange}
              required
              disabled={loading}
              className="form-input"
              placeholder="e.g., AZN, USD, EUR"
            />
          </div>

          <div className="form-group">
            <label htmlFor="NameSurname">
              Name Surname <span className="required">*</span>
            </label>
            <input
              type="text"
              id="NameSurname"
              name="NameSurname"
              value={formData.NameSurname}
              onChange={handleInputChange}
              required
              disabled={loading}
              className="form-input"
            />
          </div>

          <div className="form-group">
            <label htmlFor="device">
              Device <span className="required">*</span>
            </label>
            <select
              id="device"
              name="device"
              value={formData.device}
              onChange={handleInputChange}
              required
              disabled={loading || devicesLoading}
              className="form-input"
            >
              <option value="">
                {devicesLoading ? 'Loading devices...' : 'Select a device'}
              </option>
              {devices.map((device, index) => {
                const deviceName = device.deviceName || device.DeviceName || device.device_name || `Device ${index + 1}`;
                const deviceRef = device.ref || device.id || '';
                return (
                  <option key={device.id || device.ref || index} value={deviceRef}>
                    {deviceName}
                  </option>
                );
              })}
            </select>
            {!devicesLoading && devices.length === 0 && (
              <span style={{ fontSize: '0.85rem', color: '#ef4444', marginTop: '4px', display: 'block' }}>
                No devices available. Please add a device first.
              </span>
            )}
          </div>

          <div className="form-group">
            <label htmlFor="status">
              Status <span className="required">*</span>
            </label>
            <select
              id="status"
              name="status"
              value={formData.status}
              onChange={handleInputChange}
              required
              disabled={loading}
              className="form-input"
            >
              <option value="active">Active</option>
              <option value="inactive">Inactive</option>
            </select>
          </div>

          {error && (
            <div className="form-error">
              {error}
            </div>
          )}

          <div className="form-actions">
            <button
              type="button"
              onClick={handleClose}
              className="btn btn-secondary"
              disabled={loading}
            >
              Cancel
            </button>
            <button
              type="submit"
              className="btn btn-primary"
              disabled={loading}
            >
              {loading ? (isEdit ? 'Updating...' : 'Creating...') : (isEdit ? 'Update' : 'Create')}
            </button>
          </div>
          </form>
        </div>
      </div>
    </DetailsPortal>
  );
};

export default AddRequisiteModal;
